// Host side of the Admin embed handshake. Must stay in sync with the iframe side in
// src/Ui/Admin/wwwroot/js/embed.js. All messages are namespaced + versioned and origin-validated
// on both sides (never '*'). The token is kept in memory only.

export const ADMIN_EMBED_CHANNEL = 'admin-embed';
export const ADMIN_EMBED_VERSION = 1;

export type AdminEmbedInbound =
    | {channel: string; v: number; kind: 'ready'}
    | {channel: string; v: number; kind: 'token-request'}
    | {channel: string; v: number; kind: 'navigate'; path: string}
    | AdminHostNavigateMessage;

/** Iframe → host: show one of the host's own pages. */
export interface AdminHostNavigateMessage {
    channel: string;
    v: number;
    kind: 'host-navigate';
    /** Absolute path within the host app, e.g. "/event/{id}/order-lines". */
    path: string;
}

export interface AdminAuthMessage {
    channel: string;
    v: number;
    kind: 'auth';
    token: string;
    expiresAt: string;
    roles: string[];
}

/** Host → iframe: show this page (deep-link or browser back/forward), without reloading the app. */
export interface AdminNavigateToMessage {
    channel: string;
    v: number;
    kind: 'navigate-to';
    /** Base-relative path without the `embed/` prefix, e.g. "contracts/{id}/templates". */
    path: string;
}

export const isAdminEmbedMessage = (data: unknown): data is AdminEmbedInbound => {
    if (!data || typeof data !== 'object') {
        return false;
    }
    const message = data as {channel?: unknown; v?: unknown};
    return message.channel === ADMIN_EMBED_CHANNEL && message.v === ADMIN_EMBED_VERSION;
};

export const buildAuthMessage = (
    token: string,
    expiresAt: string,
    roles: string[]
): AdminAuthMessage => ({
    channel: ADMIN_EMBED_CHANNEL,
    v: ADMIN_EMBED_VERSION,
    kind: 'auth',
    token,
    expiresAt,
    roles,
});

export const buildNavigateToMessage = (path: string): AdminNavigateToMessage => ({
    channel: ADMIN_EMBED_CHANNEL,
    v: ADMIN_EMBED_VERSION,
    kind: 'navigate-to',
    path,
});
